import { LayoutController } from "onearm";
import { InfoTexts } from "onearm";

/**
 * @class InfoDisplayController
 * @description Controller for displaying balance, bet, win and info texts
 */
export class InfoDisplayController extends LayoutController {
    init() {
        this.currencyFormatter = this.options.currencyFormatter;
        this.gameConfig = this.options.gameConfig;

        this._balance = 0;
        this._bet = 0;

        this.setupInfoTexts();
        this.setupInfoPaysTextBlock();
    }

    setupInfoTexts() {
        const containers = this.findAll("InfoTextContainer");
        if (containers.length > 0) {
            this.infoTexts = new InfoTexts(containers, {
                currencyFormatter: this.currencyFormatter,
                gameConfig: this.gameConfig,
            });
        }
    }

    setupInfoPaysTextBlock() {
        this.forAll("InfoPaysTextBlock", (paysInfo) => {
            paysInfo.updateConfig({
                elements: [{ type: "Text", name: "PaysInfoText", text: "PaysInfoText" }],
            });
        });
    }

    formatCurrency(value) {
        return this.currencyFormatter?.format(value) ?? String(value);
    }

    /**
     * @param {number} value - Bet amount
     */
    setBet(value) {
        this._bet = value;
        const formatted = this.formatCurrency(value);
        this.forAll("BetText", (text) => {
            text.text = formatted;
        });
    }

    /**
     * @param {number} value - Balance amount
     */
    setBalance(value) {
        this._balance = value;
        const formatted = this.formatCurrency(value);
        this.forAll("BalanceText", (text) => {
            text.text = formatted;
        });
    }

    /**
     * @param {number} value - Win amount
     */
    setWin(value) {
        this.infoTexts?.setWin(value);
    }

    /**
     * @param {string} text - Info text to display
     */
    showInfo(text = "") {
        this.infoTexts?.setNormalState(text);
    }

    /**
     * @param {Object} parameters - Win info parameters
     */
    showWinInfo(parameters) {
        this.infoTexts?.setWinState(parameters);
    }

    /**
     * @param {string} text - Info text
     */
    setInfoText(text) {
        this.infoTexts?.setInfoText(text);
    }

    /**
     * @param {string} text - Pays text
     */
    setPaysText(text) {
        this.infoTexts?.setPaysText(text);
    }

    clearPayInfo() {
        this.infoTexts?.setPaysText();
    }

    /**
     * @param {Object} pay - Pay info object
     */
    showPayInfo(pay) {
        return this.infoTexts?.showPayInfo(pay);
    }

    /**
     * @param {number} value - Free spins count
     */
    setFreeSpinsCounter(value) {
        this.infoTexts?.setPaysText(`FREE SPINS LEFT ${value}`);
    }

    /**
     * @param {number} value - Free spins count for popup
     */
    setFreeSpins(value) {
        this.forAll("free_spins_text", (obj) => {
            if (obj) {
                obj.text = value;
            }
        });

        this.forAll("freeSpinsCountContainer", (obj) => {
            if (obj) {
                obj.layout();
            }
        });
        this.forAll("buyBonusPopupContent", (obj) => {
            if (obj) {
                obj.layout();
            }
        });
    }

    /**
     * @param {number} price - Selected price for free spins
     */
    setSelectedPrice(price) {
        const formattedPrice = this.formatCurrency(price);

        this.forAll(["coast_text", "cost_free_spins_text"], (obj) => obj.text = formattedPrice);

        this.forAll("freeSpinsCoastContainer", (obj) => {
            if (obj) {
                obj.layout();
            }
        });
        this.forAll("buyBonusPopupContent", (obj) => {
            if (obj) {
                obj.layout();
            }
        });
    }

    get balance() {
        return this._balance;
    }

    get bet() {
        return this._bet;
    }

    /**
     * @description Gets global position of tumbleWinValue element for fly animation target
     * @returns {{x: number, y: number}|null} Global position or null if element not found
     */
    getTumbleWinTargetPosition() {
        const tumbleWinValue = this.find("tumbleWinValue");
        if (!tumbleWinValue) {
            return null;
        }
        return tumbleWinValue.toGlobal({ x: 0, y: 0 });
    }

    /**
     * @description Sets tumble win text with multiplier format (e.g. "$10 x 2")
     * @param {number} baseWin - Base win before multiplier
     * @param {number} currentMultiplier - Current accumulated multiplier
     */
    showTumbleWinMultiplier(baseWin, currentMultiplier) {
        const formattedWin = this.formatCurrency(baseWin);
        this.forAll("tumbleWinValue", (text) => {
            text.text = `${formattedWin} x ${currentMultiplier}`;
        });
    }

    /**
     * @description Sets final tumble win value
     * @param {number} finalWin - Final win after all multipliers applied
     */
    finalizeTumbleWin(finalWin) {
        const formattedWin = this.formatCurrency(finalWin);
        this.forAll("tumbleWinValue", (text) => {
            text.text = formattedWin;
        });
    }
}

